import React, { FC, memo } from "react";
import { useTranslation } from "react-i18next";

import { Header } from "@/components/Header";
import { Section } from "@/components/Section";
import { Text } from "@/components/Text";
import { IConsultantProfile, TCompletenessItemKey } from "@/data/consultant-profiles";
import { Flex, IModalContainer, inDialogModal } from "@/eworkitComponents";
import { CloseButton } from "@/views/components/CloseButton";
import { AvatarEdit } from "@/views/ConsultantProfiles/Detail/AvatarSection/AvatarForm";

import {
  AboutMeFormik,
  CvFormik,
  LanguagesFormik,
  LocationFormik,
  PhoneFormik,
  RoleFormik,
  SkillsFormik,
} from "./forms";

interface ICompletenessModalComponentProps extends IModalContainer {
  section: TCompletenessItemKey | null;
  profile: IConsultantProfile;
  update: (profile: IConsultantProfile) => void;
}

const CompletenessModalComponent: FC<ICompletenessModalComponentProps> = memo(
  ({ section, profile, onClose, update }) => {
    const { t } = useTranslation("session");
    const { avatar, aboutMe, firstName, lastName, cvs, languages, location, phone, roles, skills, position } = profile;

    const renderForm = (key: TCompletenessItemKey | null) => {
      switch (key) {
        case "AVATAR":
          return (
            <AvatarEdit
              source={profile}
              initialValues={{ avatar, firstName, lastName }}
              onUpdate={update}
              onCancel={onClose}
            />
          );
        case "ABOUT":
          return <AboutMeFormik initialValues={{ aboutMe }} source={profile} onCancel={onClose} onUpdate={update} />;
        case "CV":
          return (
            <CvFormik
              initialValues={{ cvs: cvs?.map(cv => ({ attachment: cv })) ?? [] }}
              source={profile}
              onCancel={onClose}
              onUpdate={update}
            />
          );
        case "LANGUAGE": {
          return (
            <LanguagesFormik initialValues={{ languages }} source={profile} onCancel={onClose} onUpdate={update} />
          );
        }
        case "LOCATION": {
          return (
            <LocationFormik
              initialValues={{ location }}
              source={profile}
              onCancel={onClose}
              onUpdate={update}
              useTracking
            />
          );
        }
        case "PHONE":
          return (
            <PhoneFormik
              initialValues={{
                phone: phone || {
                  phoneCode: null,
                },
              }}
              source={profile}
              onCancel={onClose}
              onUpdate={update}
              useTracking
            />
          );
        case "ROLE":
          return (
            <RoleFormik
              initialValues={{ roles: roles || [{ role: null, roleLevel: null }] }}
              source={profile}
              onCancel={onClose}
              onUpdate={update}
            />
          );
        case "POSITION_AND_ROLE":
          return (
            <RoleFormik
              initialValues={{ position, roles: roles || [{ role: null, roleLevel: null }] }}
              showCompanyPosition
              source={profile}
              onCancel={onClose}
              onUpdate={update}
            />
          );
        case "SKILL":
          return <SkillsFormik initialValues={{ skills }} source={profile} onCancel={onClose} onUpdate={update} />;
        default:
          return null;
      }
    };

    return (
      <Section borderRadius padding={["xl"]} innerMargin="lg">
        <Flex justify="space-between">
          <Header weight="bold">{t(`CONSULTANT_PROFILES.COMPLETENESS.CONTENT.PROFILE.${section}.MODAL.TITLE`)}</Header>
          <CloseButton onClick={onClose} />
        </Flex>
        <Flex>
          <Text>{t(`CONSULTANT_PROFILES.COMPLETENESS.CONTENT.PROFILE.${section}.MODAL.DESC`)}</Text>
        </Flex>
        <Flex>{renderForm(section)}</Flex>
      </Section>
    );
  },
);

export const CompletenessModal = inDialogModal(CompletenessModalComponent);
